import React, { useState, useEffect } from "react";
import {
  Users,
  DollarSign,
  Save,
  ArrowRight,
  Search,
  AlertCircle,
  CheckCircle,
  User,
  MapPin,
  Briefcase,
  Hash,
  Edit2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiService from "../services/api";
import { useAuth } from "../context/AuthContext";
import EmployeeModal from "../components/EmployeeModal";
import "../styles/EmployeeMissingRates.css";

const EmployeeMissingRates = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingRates, setEditingRates] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [savedIds, setSavedIds] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await apiService.getEmployeesMissingRates(
        user?.partnership_id,
      );
      if (response.success) {
        setEmployees(response.employees || []);
        // Initialize editing rates
        const initialRates = {};
        response.employees?.forEach((emp) => {
          initialRates[emp.employee_id] = {
            hour_rate: emp.hour_rate || "",
            weekend_hour_rate: emp.weekend_hour_rate || "",
          };
        });
        setEditingRates(initialRates);
      }
    } catch (error) {
      console.error("Failed to load employees:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRateChange = (employeeId, field, value) => {
    setEditingRates((prev) => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [field]: value,
      },
    }));
  };

  const handleSaveRates = async (employeeId) => {
    const rates = editingRates[employeeId];
    if (!rates.hour_rate && !rates.weekend_hour_rate) {
      alert("יש להזין לפחות תעריף אחד");
      return;
    }

    try {
      setSavingId(employeeId);
      const response = await apiService.updateEmployeeRates(employeeId, {
        hour_rate: parseFloat(rates.hour_rate) || 0,
        weekend_hour_rate: parseFloat(rates.weekend_hour_rate) || 0,
      });

      if (response.success) {
        setSavedIds((prev) => [...prev, employeeId]);
        // Remove from list after short delay
        setTimeout(() => {
          setEmployees((prev) =>
            prev.filter((emp) => emp.employee_id !== employeeId),
          );
          setSavedIds((prev) => prev.filter((id) => id !== employeeId));
        }, 1500);
      } else {
        alert(response.error || "שגיאה בשמירת התעריפים");
      }
    } catch (error) {
      console.error("Failed to save rates:", error);
      alert("שגיאה בשמירת התעריפים");
    } finally {
      setSavingId(null);
    }
  };

  const handleEditEmployee = async (emp) => {
    try {
      const response = await apiService.getEmployee(emp.employee_id);
      const fullEmployee = response.data || response.employee || response;
      setSelectedEmployee({ ...emp, ...fullEmployee });
      setShowEditModal(true);
    } catch (error) {
      console.error("Failed to load employee details:", error);
      setSelectedEmployee(emp);
      setShowEditModal(true);
    }
  };

  const handleModalClose = (shouldReload) => {
    setShowEditModal(false);
    setSelectedEmployee(null);
    if (shouldReload) {
      loadEmployees();
    }
  };

  const filteredEmployees = employees.filter(
    (emp) =>
      emp.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.employee_number?.includes(searchTerm) ||
      emp.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.site_name?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <div className='missing-rates-container'>
      <div className='missing-rates-header'>
        <button className='back-btn' onClick={() => navigate("/employees")}>
          <ArrowRight size={20} />
          חזרה לעובדים
        </button>

        <div className='header-content'>
          <h1>
            <DollarSign className='header-icon' />
            עובדים ללא תעריפים
          </h1>
          <p className='header-subtitle'>
            עובדים שחסרים להם תעריף שעתי או תעריף סופ״ש
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className='stats-bar'>
        <div className='stat-item'>
          <AlertCircle size={20} />
          <span>
            <strong>{employees.length}</strong> עובדים ללא תעריפים
          </span>
        </div>
      </div>

      {/* Search */}
      <div className='search-section'>
        <div className='search-box'>
          <Search size={18} />
          <input
            type='text'
            placeholder='חיפוש לפי שם, מספר עובד, לקוח או אתר...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Employee List */}
      {loading ? (
        <div className='loading-state'>
          <div className='spinner'></div>
          <p>טוען עובדים...</p>
        </div>
      ) : filteredEmployees.length === 0 ? (
        <div className='empty-state'>
          <CheckCircle size={48} />
          <h3>אין עובדים ללא תעריפים</h3>
          <p>כל העובדים מוגדרים עם תעריפים</p>
        </div>
      ) : (
        <div className='employees-grid'>
          {filteredEmployees.map((emp, index) => (
            <div
              key={emp.employee_id}
              className={`employee-card ${
                savedIds.includes(emp.employee_id) ? "saved" : ""
              }`}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className='card-header'>
                <div className='avatar'>
                  <User size={28} />
                </div>
                <div className='header-info'>
                  <h3>{emp.employee_name}</h3>
                  {emp.employee_number && (
                    <span className='employee-number'>
                      <Hash size={12} />
                      {emp.employee_number}
                    </span>
                  )}
                </div>
                <div className='card-status'>
                  <AlertCircle size={20} />
                </div>
              </div>

              <div className='card-body'>
                <div className='info-tags'>
                  {emp.customer_name && (
                    <div className='info-tag customer'>
                      <Briefcase size={14} />
                      <span>{emp.customer_name}</span>
                    </div>
                  )}
                  {emp.site_name && (
                    <div className='info-tag site'>
                      <MapPin size={14} />
                      <span>{emp.site_name}</span>
                    </div>
                  )}
                  {emp.type && (
                    <div className='info-tag type'>
                      <Users size={14} />
                      <span>{emp.type}</span>
                    </div>
                  )}
                </div>

                <div className='rates-section'>
                  <div className='rates-header'>
                    <DollarSign size={16} />
                    <span>הגדר תעריפים</span>
                  </div>
                  <div className='rates-inputs'>
                    <div className='rate-field'>
                      <label>רגיל</label>
                      <div className='input-wrapper'>
                        <span className='currency'>₪</span>
                        <input
                          type='number'
                          step='0.01'
                          min='0'
                          placeholder='0.00'
                          value={editingRates[emp.employee_id]?.hour_rate || ""}
                          onChange={(e) =>
                            handleRateChange(
                              emp.employee_id,
                              "hour_rate",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </div>
                    <div className='rate-field'>
                      <label>סופ״ש</label>
                      <div className='input-wrapper'>
                        <span className='currency'>₪</span>
                        <input
                          type='number'
                          step='0.01'
                          min='0'
                          placeholder='0.00'
                          value={
                            editingRates[emp.employee_id]?.weekend_hour_rate ||
                            ""
                          }
                          onChange={(e) =>
                            handleRateChange(
                              emp.employee_id,
                              "weekend_hour_rate",
                              e.target.value,
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className='card-footer'>
                <div className='card-footer-buttons'>
                  <button
                    className={`save-btn ${
                      savedIds.includes(emp.employee_id) ? "saved" : ""
                    }`}
                    onClick={() => handleSaveRates(emp.employee_id)}
                    disabled={savingId === emp.employee_id}
                  >
                    {savingId === emp.employee_id ? (
                      <span className='loading'>שומר...</span>
                    ) : savedIds.includes(emp.employee_id) ? (
                      <>
                        <CheckCircle size={18} />
                        <span>נשמר בהצלחה!</span>
                      </>
                    ) : (
                      <>
                        <Save size={18} />
                        <span>שמור תעריפים</span>
                      </>
                    )}
                  </button>
                  <button
                    className='edit-employee-btn'
                    onClick={() => handleEditEmployee(emp)}
                    title='ערוך עובד'
                  >
                    <Edit2 size={18} />
                    <span>ערוך עובד</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {showEditModal && (
        <EmployeeModal employee={selectedEmployee} onClose={handleModalClose} />
      )}
    </div>
  );
};

export default EmployeeMissingRates;
